import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Lets a customer swap the color of an existing cart line without returning
 * to the product page.
 *
 * No native "swap the variant of an existing line" endpoint exists, so this
 * is implemented as add-then-remove: the new variant is added FIRST via
 * /cart/add.js, and the old line is only removed via /cart/change.js after
 * that succeeds. A failed second step therefore never loses the product
 * entirely - worst case is "both colors briefly in the cart" (clearly
 * surfaced to the customer via #status), never "product vanished".
 *
 * Deliberately reuses the exact same native primitives already used
 * elsewhere in the cart (the quantity selector in component-cart-items.js,
 * the add-to-cart flow in product-form.js): the same endpoints, and the
 * same CartLinesUpdateEvent contract that cart-items-component already
 * listens for globally (document-level 'shopify:cart:lines-update') to
 * refresh every cart surface on the page (drawer, cart page, header
 * bubble). No bespoke DOM-patching/morphing code here at all - resolving
 * the event's promise with the standard {cart, detail: {sections, ...}}
 * shape is enough for the existing listener to do the rest.
 *
 * Known limitation (disclosed, not a bug): the swapped-in line lands at the
 * bottom of the cart (it's a new line), not in the old line's position.
 * Quantity is preserved.
 *
 * BUG FOUND AND FIXED (real-world: worked in isolated cart-API testing, did
 * nothing when actually clicked on the live site): the option buttons'
 * on:click used to omit a selector ("/selectColor/..."). Without one,
 * component.js resolves the target via getClosestComponent(), which walks
 * up to the NEAREST "-component" ancestor - here that's the nested
 * anchored-popover-component (it wraps the option buttons), not this
 * component, even though this is where selectColor() lives. The popover
 * component has no such method, so the click was silently dropped - no
 * error, no request, nothing to see in the network tab. Fixed by naming
 * this element's tag explicitly in on:click so closest() skips the
 * intervening popover. Same nested-component scoping also broke
 * `this.refs.popover` below (that ref belongs to the popover component's
 * own scope, not this one's) - fixed by walking up from the clicked
 * element instead of trusting this.refs for it.
 *
 * BUG FOUND AND FIXED (add.js fired correctly, change.js never did): POSTing
 * {items: [...], sections: ...} to /cart/add.js - the batch-add shape, used
 * so quantity/variant are both controlled directly rather than via a form -
 * returns ONLY {items, sections}. Confirmed directly against the live cart:
 * no token, no total_price, no currency, no item_count - all fields a
 * "full cart" ajax response normally has, and exactly what
 * CartLinesUpdateEvent.createCartFromAjaxResponse presumably expects to
 * build its {id, totalQuantity, cost, ...} shape from. It very plausibly
 * throws on this leaner shape; since that call sat directly in the .then()
 * callback, ahead of the call to #removeLine(), a throw there jumped
 * straight to .catch() and #removeLine() never ran - add.js firing but
 * change.js never following, exactly as reported, with the error going to
 * console.error (not necessarily checked, since the report focused on
 * Network). #safeCartFromAjaxResponse() (now #resolveWithFullCart, see
 * below) isolates that conversion so a failure there can never again block
 * the actual removal - the cart list (detail.sections/detail.items), which
 * is all cart-items-component's own listener actually reads, doesn't depend
 * on it either.
 *
 * FOLLOW-UP (found while investigating a separate report - Shopify's native
 * analytics/conversion funnel not registering add-to-cart actions taken
 * through this widget): the fix above only stopped the leaner response from
 * BLOCKING the rest of the chain - it still meant the event's promise
 * resolved with cart:null every time, not just on the rare genuine error.
 * assets/product-form.js (the native add-to-cart form, never touched here)
 * does NOT have this gap: it calls a separate #refreshCart() - a full
 * /cart.json fetch - and converts THAT, never the lean /cart/add.js
 * response directly. Whether Shopify's own analytics actually depends on
 * this event/cart payload at all isn't something visible from the theme
 * side - but resolving with real data instead of null unconditionally
 * brings this in line with the native, working pattern rather than leaving
 * that gap open on a guess. #resolveWithFullCart() below does the same
 * extra fetch, fire-and-forget (never awaited before #removeLine()), so the
 * original guarantee - removal never blocks on this - still holds.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} status - Hidden live region for success/error messaging.
 *
 * @extends {Component<Refs>}
 */
export class CartLineColorSwapComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    // BUG FOUND AND FIXED (3rd real-world report of "clicking a color does
    // nothing" - this time confirmed via the customer's own DevTools that
    // it's NOT the earlier on:click/ref bug: no console warning, no network
    // request at all, meaning the popover itself never visibly opened).
    // Root cause: this popover was positioned entirely with CSS anchor
    // positioning (anchor-name/position-anchor/anchor()), with a JS fallback
    // (anchored-popover.js) for browsers without native support. Neither
    // path was ever visually verified (no way to render a page here), and
    // the popover element never had an explicit `position` declared in this
    // file's own CSS - it relied entirely on the UA's built-in popover
    // default. That's plausibly correct, but is exactly the kind of subtle,
    // hard-to-verify-without-a-browser detail that has already broken twice
    // on this feature. Replaced with the simplest thing that can't hide a
    // bug this way: compute the position with getBoundingClientRect() and
    // set plain top/left inline styles by hand, in code that's trivial to
    // read and reason about without needing to render it.
    const popover = this.querySelector('[popover]');
    const trigger = this.querySelector('[popovertarget]');
    if (popover instanceof HTMLElement && trigger instanceof HTMLElement) {
      popover.addEventListener('beforetoggle', (event) => {
        if (!(event instanceof ToggleEvent) || event.newState !== 'open') return;
        this.#positionPopover(popover, trigger);
      });
    }
  }

  /**
   * Positions the popover directly below its trigger, clamped to stay within
   * the viewport horizontally. Plain fixed-position math - deliberately not
   * relying on CSS anchor positioning (see connectedCallback comment above).
   * @param {HTMLElement} popover
   * @param {HTMLElement} trigger
   */
  #positionPopover(popover, trigger) {
    const spacing = 6;
    const viewportMargin = 16;
    const maxWidth = 220;

    const rect = trigger.getBoundingClientRect();
    const left = Math.max(viewportMargin, Math.min(rect.left, window.innerWidth - maxWidth - viewportMargin));

    popover.style.position = 'fixed';
    popover.style.top = `${rect.bottom + spacing}px`;
    popover.style.left = `${left}px`;
  }

  /**
   * Handles a click on one of the alternative color options.
   * @param {number} variantId - The new variant's numeric ID (parsed from the on:click data segment).
   * @param {PointerEvent} event
   */
  selectColor(variantId, event) {
    event.preventDefault();

    if (this.hasAttribute('data-loading')) return;

    const { lineKey, quantity } = this.dataset;
    if (!lineKey || !quantity) return;

    // Not this.refs.popover: the popover element belongs to the NESTED
    // anchored-popover-component's own ref scope (Component only assigns a
    // [ref] to the closest "-component" ancestor), not to this outer
    // component's refs - it would silently be undefined here. Walking up
    // from the clicked option button finds it regardless of which
    // component "owns" the ref.
    if (event.target instanceof Element) {
      const popover = event.target.closest('[popover]');
      if (popover instanceof HTMLElement) popover.hidePopover();
    }
    this.setAttribute('data-loading', '');
    this.#setStatus('');

    const addPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId.toString(), quantity: Number(quantity) }],
        promise: addPromise.promise,
      })
    );

    fetch(
      Theme.routes.cart_add_url,
      fetchConfig('json', {
        body: JSON.stringify({
          items: [{ id: variantId, quantity: Number(quantity) }],
          sections: this.#cartSectionIds().join(','),
        }),
      })
    )
      .then((response) => response.json())
      .then((response) => {
        // Shopify's /cart/add.js returns a truthy "status" (e.g. 422) plus a
        // "message" on failure - same check used by the native add-to-cart
        // flow (assets/product-form.js).
        if (response.status) {
          addPromise.reject(new Error(response.message || 'Add to cart failed'));
          this.#setStatus(response.message || 'Impossible d’ajouter cette couleur au panier.');
          return null;
        }

        // Fire-and-forget: resolves addPromise once a full cart fetch comes
        // back, but never awaited here - #removeLine() below must never wait
        // on it, see class-level bug note.
        this.#resolveWithFullCart(addPromise, response);

        // Only remove the old line once the new one is confirmed in the cart.
        return this.#removeLine(lineKey);
      })
      .catch((error) => {
        console.error('[cart-line-color-swap]', error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Network error while swapping color',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
        this.#setStatus('Une erreur réseau est survenue, merci de réessayer.');
      })
      .finally(() => {
        this.removeAttribute('data-loading');
      });
  }

  /**
   * Removes the original line now that the replacement variant has been added.
   * Mirrors component-cart-items.js's onLineItemRemove/updateQuantity pattern,
   * using the line's unique key (robust to position shifts) rather than a
   * 1-based line index.
   * @param {string} lineKey
   * @returns {Promise<void>}
   */
  #removeLine(lineKey) {
    const removePromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: [{ id: lineKey, quantity: 0 }],
        promise: removePromise.promise,
      })
    );

    return fetch(
      Theme.routes.cart_change_url,
      fetchConfig('json', {
        body: JSON.stringify({
          id: lineKey,
          quantity: 0,
          sections: this.#cartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      })
    )
      .then((response) => response.json())
      .then((response) => {
        if (response.errors) {
          const message = typeof response.errors === 'string' ? response.errors : 'Cart update failed';
          removePromise.reject(new Error(message));
          // The add already succeeded and already refreshed the UI - the
          // customer now sees BOTH colors in the cart. Surface this clearly
          // rather than silently leaving an unexplained duplicate line.
          this.#setStatus(
            'La nouvelle couleur a été ajoutée, mais l’ancienne ligne n’a pas pu être retirée automatiquement. Merci de la supprimer manuellement.'
          );
          return;
        }

        this.#resolveWithFullCart(removePromise, response);
      })
      .catch((error) => {
        console.error('[cart-line-color-swap] remove failed after add succeeded', error);
        removePromise.reject(error);
        this.#setStatus(
          'La nouvelle couleur a été ajoutée, mais l’ancienne ligne n’a pas pu être retirée automatiquement. Merci de la supprimer manuellement.'
        );
      });
  }

  /**
   * Resolves a CartLinesUpdateEvent's promise with cart data built from a
   * FULL /cart.json fetch - not the response of the add.js/change.js call
   * that triggered it, which is missing fields (see class doc). Mirrors
   * assets/product-form.js's #refreshCart(). Deliberately not awaited by
   * callers: this must never delay or block whatever happens next (e.g.
   * #removeLine() in selectColor() above) - it only resolves a promise
   * other listeners (cart-items-component, potentially Shopify's own
   * analytics) are watching, nothing here depends on its result.
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} promiseHandle
   * @param {{sections?: unknown, items?: unknown}} addOrChangeResponse
   */
  async #resolveWithFullCart(promiseHandle, addOrChangeResponse) {
    let cart = null;
    try {
      const fullCart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => response.json());
      cart = CartLinesUpdateEvent.createCartFromAjaxResponse(fullCart);
    } catch (error) {
      console.warn('[cart-line-color-swap] full cart fetch/conversion failed, continuing anyway', error);
    }

    promiseHandle.resolve({
      cart,
      detail: {
        sections: addOrChangeResponse.sections,
        items: addOrChangeResponse.items,
        source: 'cart-line-color-swap',
        didError: false,
      },
    });
  }

  /** @returns {string[]} Section IDs of every cart-items-component on the page. */
  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(/** @returns {id is string} */ (id) => Boolean(id));
  }

  /** @param {string} message */
  #setStatus(message) {
    const { status } = this.refs;
    if (status) status.textContent = message;
  }
}

if (!customElements.get('cart-line-color-swap-component')) {
  customElements.define('cart-line-color-swap-component', CartLineColorSwapComponent);
}
